import { HttpError } from '@euler/lib/request/HttpError';
import { BiAnswer, BiAnswerAction } from '@euler/model/bi/answer';
import {
  BiDatasetGrantView,
  BiDatasetVisibility,
  BiUploadedDatasetView,
} from '@euler/model/bi/dataset';
import { BiDraftDetail } from '@euler/model/bi/draft';
import {
  BiDatasetDetail,
  BiDatasetGrantInput,
  BiDatasetList,
  BiDatasetPreview,
  BiDatasetSection,
  BiDatasetSharingResult,
  BiDatasetUsage,
  BiListDatasetsRequest,
  BiUpdateDatasetRequest,
} from '@euler/model/bi/library';
import { BiDatasetRefKind } from '@euler/model/bi/query';
import {
  BiSheetUploadView,
  BiUploadStatus,
  BiUploadView,
} from '@euler/model/bi/upload';
import { kBiBuiltinDatasetsFixture } from '@euler/playground/bi-dashboards/biBuiltinDatasetsFixture';
import { BiService } from '@euler/services/bi.service';
import { kBiUploadFixtureMembers } from './FixtureUploadStoreService';
import {
  kCapturedDeleteRefusal,
  kCapturedDetails,
  kCapturedDuplicateUpload,
  kCapturedFailedUpload,
  kCapturedPreview,
  kCapturedProposals,
  kCapturedSharedPreview,
  kCapturedSplitUpload,
} from './biUploadsCaptured';

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

function notFound(): HttpError {
  const error = new HttpError('记录不存在!');
  error.code = 'resource_not_found';
  error.subCode = 'record_not_found';
  error.status = 404;
  return error;
}

/**
 * The captured uploads and datasets in memory, so the widgets and the
 * library render every state without a server. A tap the playground
 * "sends" moves the upload on the way the importer would, step by step.
 */
export class FixtureBiUploadService extends BiService {
  private readonly uploadViews = new Map<string, BiUploadView>(
    [kCapturedSplitUpload, kCapturedDuplicateUpload, kCapturedFailedUpload].map(
      view => [view.id, clone(view)],
    ),
  );
  private readonly drafts = new Map<string, BiDraftDetail>(
    kCapturedProposals.map(draft => [draft.id, clone(draft)]),
  );
  private readonly details = new Map<string, BiDatasetDetail>(
    kCapturedDetails.map(detail => [
      detail.kind === BiDatasetRefKind.Upload ? detail.dataset.id : '',
      clone(detail),
    ]),
  );
  private readonly grants = new Map<string, BiDatasetGrantView[]>();
  private created = 0;

  constructor() {
    super(null as never);
  }

  async upload(uploadId: string): Promise<BiUploadView> {
    await delay(200);
    const view = this.uploadViews.get(uploadId);
    if (!view) throw notFound();
    return clone(view);
  }
  async uploads(): Promise<BiUploadView[]> {
    await delay(200);
    return [...this.uploadViews.values()].reverse().map(clone);
  }
  async draft(draftId: string): Promise<BiDraftDetail> {
    await delay(250);
    const draft = this.drafts.get(draftId);
    if (!draft) throw notFound();
    return clone(draft);
  }
  async listDatasets(request: BiListDatasetsRequest): Promise<BiDatasetList> {
    await delay(200);
    if (request.section === BiDatasetSection.Builtin)
      return kBiBuiltinDatasetsFixture;
    const mine = request.section === BiDatasetSection.Mine;
    return {
      section: request.section,
      datasets: this.uploaded()
        .filter(dataset => (dataset.ownerMemberId === 3855) === mine)
        .map(dataset => ({ kind: BiDatasetRefKind.Upload, dataset })),
    };
  }
  async getDataset(ref: string): Promise<BiDatasetDetail> {
    await delay(200);
    const detail = this.details.get(ref);
    if (detail) return clone(detail);
    const builtin = kBiBuiltinDatasetsFixture.datasets.find(
      entry =>
        entry.kind === BiDatasetRefKind.Sql &&
        entry.catalog.dataset.kind === BiDatasetRefKind.Sql &&
        entry.catalog.dataset.model === ref,
    );
    if (builtin?.kind === BiDatasetRefKind.Sql) return builtin;
    throw notFound();
  }
  async previewDataset(datasetId: string): Promise<BiDatasetPreview> {
    await delay(400);
    return this.ownedBy(datasetId) === 3855
      ? kCapturedPreview
      : kCapturedSharedPreview;
  }
  async datasetUsage(datasetId: string): Promise<BiDatasetUsage> {
    return this.uploadedDetail(datasetId).usageDetail;
  }
  async updateDataset(
    datasetId: string,
    request: BiUpdateDatasetRequest,
  ): Promise<BiUploadedDatasetView> {
    await delay(300);
    const detail = this.uploadedDetail(datasetId);
    if (request.name !== undefined) {
      detail.dataset.name = request.name;
      detail.catalog.label = { zh: request.name, en: request.name };
    }
    if (request.description !== undefined) {
      detail.dataset.description = request.description;
      detail.catalog.description = {
        zh: request.description ?? '',
        en: request.description ?? '',
      };
    }
    return clone(detail.dataset);
  }
  async setDatasetVisibility(
    datasetId: string,
    visibility: BiDatasetVisibility,
    confirmed: boolean,
  ): Promise<BiDatasetSharingResult> {
    await delay(300);
    const detail = this.uploadedDetail(datasetId);
    const widens =
      visibility === BiDatasetVisibility.Store &&
      detail.dataset.visibility !== BiDatasetVisibility.Store;
    const applied = !widens || confirmed;
    if (applied) detail.dataset.visibility = visibility;
    return this.sharing(datasetId, applied, widens, visibility);
  }
  async datasetGrants(datasetId: string): Promise<BiDatasetGrantView[]> {
    await delay(200);
    return clone(this.grants.get(datasetId) ?? []);
  }
  async replaceDatasetGrants(
    datasetId: string,
    grants: BiDatasetGrantInput[],
    confirmed: boolean,
  ): Promise<BiDatasetSharingResult> {
    await delay(300);
    const before = this.grants.get(datasetId) ?? [];
    const widens = grants.some(grant => {
      const had = before.find(entry => entry.memberId === grant.memberId);
      return !had || had.permission < grant.permission;
    });
    const applied = !widens || confirmed;
    if (applied)
      this.grants.set(
        datasetId,
        grants.map(grant => ({
          ...grant,
          memberName: this.nameOf(grant.memberId),
          createdBy: 3855,
          createdAt: new Date().toISOString(),
        })),
      );
    const detail = this.uploadedDetail(datasetId);
    const result = this.sharing(
      datasetId,
      applied,
      widens,
      detail.dataset.visibility,
    );
    if (!applied && !result.audience.everyone)
      result.audience.members = [
        { memberId: 3855, name: this.nameOf(3855) },
        ...grants.map(grant => ({
          memberId: grant.memberId,
          name: this.nameOf(grant.memberId),
        })),
      ];
    return result;
  }
  async deleteDataset(datasetId: string): Promise<void> {
    await delay(300);
    const detail = this.uploadedDetail(datasetId);
    if (detail.usageDetail.cardCount) {
      const { count, dashboards } = kCapturedDeleteRefusal.i18n;
      // The euler edge renders the catalogued public copy (zh.json).
      const error = new HttpError(
        `这份数据还在 ${count} 个看板上使用（${dashboards}），请先从这些看板移除相关图表再删除。`,
      );
      error.code = kCapturedDeleteRefusal.code;
      error.subCode = kCapturedDeleteRefusal.subCode;
      error.status = 409;
      throw error;
    }
    this.details.delete(datasetId);
  }

  /** What the server does with a tap, then the reply's own delay. */
  async settle(answer: BiAnswer): Promise<void> {
    switch (answer.action) {
      case BiAnswerAction.SelectSheets:
        this.selectSheets(answer.uploadId, answer.sheets);
        break;
      case BiAnswerAction.ConfirmUpload:
        this.confirm(answer.uploadId);
        break;
      case BiAnswerAction.Add:
        for (const id of answer.draftIds) {
          const draft = this.drafts.get(id);
          if (draft)
            draft.addedTo = [
              {
                dashboardId: 'bidash_01m53sz7j6gsbwn9w8jbkv0g7y',
                dashboardName: '采购分析',
                cardId: `bicard_${id.slice(8)}`,
              },
            ];
        }
        break;
      default:
        break;
    }
    await delay(800);
  }

  /** The file upload a sheet upload belongs to. */
  fileUploadOf(sheetUploadId: string): string {
    for (const view of this.uploadViews.values())
      if (view.sheets.some(sheet => sheet.upload?.id === sheetUploadId))
        return view.id;
    return sheetUploadId;
  }

  private selectSheets(uploadId: string, sheets: string[]) {
    const view = this.uploadViews.get(uploadId);
    if (!view) return;
    view.status = BiUploadStatus.Split;
    for (const sheet of view.sheets)
      if (sheets.includes(sheet.name) && !sheet.upload) {
        const template = kCapturedSplitUpload.sheets.find(
          entry => entry.name === sheet.name,
        )?.upload;
        const upload: BiSheetUploadView = {
          id: `biupl_${`fx${this.created++}`.padEnd(26, '0')}`,
          sheet: sheet.name,
          status: BiUploadStatus.Uploaded,
          generation: 1,
          schema: null,
          datasetId: null,
          proposalDraftIds: [],
          failure: null,
        };
        sheet.upload = upload;
        this.later(1500, () => {
          upload.status = BiUploadStatus.NeedsConfirmation;
          upload.schema = clone(
            template?.schema ?? kCapturedSplitUpload.sheets[1].upload!.schema,
          );
        });
      }
  }

  private confirm(sheetUploadId: string) {
    for (const view of this.uploadViews.values())
      for (const sheet of view.sheets) {
        const upload = sheet.upload;
        if (
          upload?.id !== sheetUploadId ||
          upload.status !== BiUploadStatus.NeedsConfirmation
        )
          continue;
        upload.status = BiUploadStatus.Confirmed;
        upload.generation += 1;
        this.later(1500, () => (upload.status = BiUploadStatus.Materialized));
        this.later(3000, () => {
          upload.status = BiUploadStatus.Ready;
          upload.proposalDraftIds =
            sheet.name === '配件采购' ? [...this.drafts.keys()] : [];
        });
      }
  }

  private later(ms: number, change: () => void) {
    setTimeout(change, ms);
  }
  private uploaded(): BiUploadedDatasetView[] {
    return [...this.details.values()].flatMap(detail =>
      detail.kind === BiDatasetRefKind.Upload ? [clone(detail.dataset)] : [],
    );
  }
  private uploadedDetail(datasetId: string) {
    const detail = this.details.get(datasetId);
    if (detail?.kind !== BiDatasetRefKind.Upload) throw notFound();
    return detail;
  }
  private ownedBy(datasetId: string): number | null {
    const detail = this.details.get(datasetId);
    return detail?.kind === BiDatasetRefKind.Upload
      ? detail.dataset.ownerMemberId
      : null;
  }
  private nameOf(memberId: number): string | null {
    return (
      kBiUploadFixtureMembers.find(member => member.id === memberId)?.name ??
      null
    );
  }
  private sharing(
    datasetId: string,
    applied: boolean,
    widens: boolean,
    visibility: BiDatasetVisibility,
  ): BiDatasetSharingResult {
    const detail = this.uploadedDetail(datasetId);
    return {
      applied,
      widens,
      audience: {
        everyone: visibility === BiDatasetVisibility.Store,
        members: [],
      },
      dataset: clone(detail.dataset),
      grants: clone(this.grants.get(datasetId) ?? []),
    };
  }
}
